import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from '../../styles/Team.module.css';

const PageDetailsModal = ({ pageData, isOpen, onClose, timeRange }) => {
  const [dailyData, setDailyData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && pageData) {
      fetchDailyData();
    }
  }, [isOpen, pageData, timeRange]);

  const fetchDailyData = async () => {
    setLoading(true);
    try {
      // Calculate date range based on timeRange
      const endDate = new Date();
      const startDate = new Date();
      
      switch (timeRange) {
        case 1: // Yesterday - only 1 day (yesterday to yesterday)
          startDate.setDate(endDate.getDate());
          endDate.setDate(endDate.getDate());
          break;
        case 7: // Last 7 days (today - 6 to today)
          startDate.setDate(endDate.getDate() - 6);
          // endDate stays as today
          break;
        case 30: // Last 30 days (today - 29 to today)
          startDate.setDate(endDate.getDate() - 29);
          // endDate stays as today
          break;
        case 90: // Last 90 days (today - 89 to today)
          startDate.setDate(endDate.getDate() - 89);
          // endDate stays as today
          break;
        default:
          startDate.setDate(endDate.getDate() - 29);
          // endDate stays as today
      }

      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      console.log('Modal Date range:', { timeRange, startDateStr, endDateStr, startDate, endDate });

      const response = await fetch(
        `https://u6rn7wtdel.execute-api.us-east-1.amazonaws.com/Prod/daily?page_id=${pageData.page_id}&start_date=${startDateStr}&end_date=${endDateStr}`
      );
      const data = await response.json();
      
      if (data.success) {
        // Filter and sort by date
        const filteredData = data.data
          .filter(item => item.page_id === pageData.page_id)
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        
        setDailyData(filteredData);
      }
    } catch (error) {
      console.error('Error fetching daily data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const calculateRPM = (earnings, views) => {
    return views > 0 ? (earnings / views) * 1000 : 0;
  };

  // Calculate totals from daily data for consistency
  const calculateTotalsFromDaily = () => {
    if (!dailyData || dailyData.length === 0) return null;
    
    return dailyData.reduce((totals, day) => {
      const earnings = parseFloat(day.approximate_monetization_earnings) || 0;
      const views = parseInt(day.page_video_views) || 0;
      const impressions = parseInt(day.page_impressions) || 0;
      
      return {
        totalEarnings: totals.totalEarnings + earnings,
        totalVideoViews: totals.totalVideoViews + views,
        totalImpressions: totals.totalImpressions + impressions
      };
    }, { totalEarnings: 0, totalVideoViews: 0, totalImpressions: 0 });
  };

  const dailyTotals = calculateTotalsFromDaily();

  // Debug logging to help identify data discrepancies
  if (dailyTotals) {
    console.log('PageDetailsModal - Daily Totals:', {
      pageId: pageData.page_id,
      pageName: pageData.page_name,
      timeRange,
      dailyTotals,
      dailyDataCount: dailyData.length,
      dataSource: 'daily_aggregated'
    });
  }

  // Debug logging for pageData
  console.log('PageDetailsModal - pageData received:', pageData);
  
  // Additional debug logging to compare table data vs modal data
  if (dailyTotals && pageData) {
    console.log('PageDetailsModal - Data Comparison:', {
      pageId: pageData.page_id,
      timeRange,
      tableData: {
        total_approximate_earnings: pageData.total_approximate_earnings,
        total_video_views: pageData.total_video_views,
        total_impressions: pageData.total_impressions,
        video_rpm: pageData.video_rpm
      },
      modalData: {
        total_approximate_earnings: dailyTotals.totalEarnings,
        total_video_views: dailyTotals.totalVideoViews,
        total_impressions: dailyTotals.totalImpressions,
        video_rpm: dailyTotals.totalVideoViews > 0 ? (dailyTotals.totalEarnings / dailyTotals.totalVideoViews * 1000) : 0
      },
      discrepancy: {
        earnings: Math.abs(pageData.total_approximate_earnings - dailyTotals.totalEarnings),
        views: Math.abs(pageData.total_video_views - dailyTotals.totalVideoViews),
        impressions: Math.abs(pageData.total_impressions - dailyTotals.totalImpressions)
      }
    });
  }

  if (!isOpen || !pageData) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.pageDetailsModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <FontAwesomeIcon icon="chart-line" />
            <h3>{pageData.page_name}</h3>
          </div>
          <button className={styles.modalCloseBtn} onClick={onClose}>
            <FontAwesomeIcon icon="times" />
          </button>
        </div>
        
        <div className={styles.modalContent}>
          {/* Page Summary */}
          <div className={styles.pageSummary}>
            <div className={styles.pageStats}>
              <div className={styles.statItem}>
                <FontAwesomeIcon icon="dollar-sign" />
                <div>
                  <span className={styles.statLabel}>Total Earnings</span>
                  <span className={styles.statValue}>
                    {dailyTotals ? formatCurrency(dailyTotals.totalEarnings) : formatCurrency(pageData.total_approximate_earnings)}
                  </span>
                </div>
              </div>
              <div className={styles.statItem}>
                <FontAwesomeIcon icon="play" />
                <div>
                  <span className={styles.statLabel}>Video Views</span>
                  <span className={styles.statValue}>
                    {dailyTotals ? formatNumber(dailyTotals.totalVideoViews) : formatNumber(pageData.total_video_views)}
                  </span>
                </div>
              </div>
              <div className={styles.statItem}>
                <FontAwesomeIcon icon="chart-line" />
                <div>
                  <span className={styles.statLabel}>Video RPM</span>
                  <span className={styles.statValue}>
                    ${dailyTotals ? (dailyTotals.totalVideoViews > 0 ? (dailyTotals.totalEarnings / dailyTotals.totalVideoViews * 1000).toFixed(2) : '0.00') : pageData.video_rpm.toFixed(2)}
                  </span>
                </div>
              </div>
              <div className={styles.statItem}>
                <FontAwesomeIcon icon="eye" />
                <div>
                  <span className={styles.statLabel}>Impressions</span>
                  <span className={styles.statValue}>
                    {dailyTotals ? formatNumber(dailyTotals.totalImpressions) : formatNumber(pageData.total_impressions)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Daily Performance */}
          <div className={styles.dailyPerformance}>
            <h4>
              <FontAwesomeIcon icon="calendar" style={{ marginRight: '8px' }} />
              Daily Performance ({timeRange === 1 ? 'Yesterday' : `Last ${timeRange} days`})
            </h4>
            
            {loading ? (
              <div className={styles.loadingSpinner}>Loading daily data...</div>
            ) : dailyData && dailyData.length > 0 ? (
              <div className={styles.dailyDataTable}>
                <div className={styles.dailyDataHeader}>
                  <div>Date</div>
                  <div>Earnings</div>
                  <div>Video Views</div>
                  <div>RPM</div>
                  <div>Impressions</div>
                </div>
                
                {dailyData.map((day, index) => {
                  const rpm = calculateRPM(
                    parseFloat(day.approximate_monetization_earnings),
                    parseInt(day.page_video_views)
                  );
                  
                  return (
                    <div key={index} className={styles.dailyDataRow}>
                      <div className={styles.dailyDate}>
                        {formatDate(day.date)}
                      </div>
                      <div className={styles.dailyEarnings}>
                        {formatCurrency(day.approximate_monetization_earnings)}
                      </div>
                      <div className={styles.dailyViews}>
                        {formatNumber(day.page_video_views)}
                      </div>
                      <div className={styles.dailyRPM}>
                        ${rpm.toFixed(2)}
                      </div>
                      <div className={styles.dailyImpressions}>
                        {formatNumber(day.page_impressions)}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <FontAwesomeIcon icon="calendar-times" />
                <p>No daily data available for this period</p>
              </div>
            )}
          </div>

          {/* Performance Insights */}
          <div className={styles.performanceInsights}>
            <h4>
              <FontAwesomeIcon icon="lightbulb" style={{ marginRight: '8px' }} />
              Performance Insights
            </h4>
            
            <div className={styles.insightsGrid}>
              <div className={styles.insightCard}>
                <div className={styles.insightIcon}>
                  <FontAwesomeIcon icon="trending-up" />
                </div>
                <div className={styles.insightContent}>
                  <h5>CTR Performance</h5>
                  <p>
                    {pageData.total_impressions > 0 
                      ? ((pageData.total_video_views / pageData.total_impressions) * 100).toFixed(2)
                      : 0
                    }% click-through rate from impressions to video views
                  </p>
                </div>
              </div>
              
              <div className={styles.insightCard}>
                <div className={styles.insightIcon}>
                  <FontAwesomeIcon icon="dollar-sign" />
                </div>
                <div className={styles.insightContent}>
                  <h5>Revenue Efficiency</h5>
                  <p>
                    ${pageData.video_rpm.toFixed(2)} earned per 1,000 video views
                  </p>
                </div>
              </div>
              
              <div className={styles.insightCard}>
                <div className={styles.insightIcon}>
                  <FontAwesomeIcon icon="users" />
                </div>
                <div className={styles.insightContent}>
                  <h5>Audience Engagement</h5>
                  <p>
                    {pageData.total_video_views.toLocaleString()} total video views
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageDetailsModal; 